import _ from 'lodash';
import React from 'react';
import {
  TextCollectionField,
  TextCollectionFieldType,
  DatetimeAnnotation,
  SelectField,
  FieldSet,
  CategoryField,
  CategoryChoiceValue,
  TextCollectionAttachment,
} from '@scale/shared/types/params/textcollection';
import {
  TextCollectionAnnotation,
  FieldSetAnnotation,
  SelectFieldAnnotation,
} from '@scale/shared/types/response/textcollection';
import { isBlankField } from '@scale/shared/textcollection/helpers';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { Checkbox } from 'design-system';
import { makeStyles, Link } from '@material-ui/core';
import { getShiftedTime } from './TimeRangeFieldSection';
import { useAnnotationLookup, AnnotationLookupResult } from './document';
import { getFieldSetTitle } from './fieldSetUtils';
import {
  getCategoryFullLabelPath,
  getCategoryPathLabelGroups,
} from '@scale/shared/textcollection/field';
import Markdown from '@scale/frontend-lib/markdown';
import classnames from 'classnames';
import BoundingBoxPreview from './BoundingBoxPreview';
import { Colors } from 'react-scale/components/legacy-css/global_css';
import { MultipleCategoryAuditStatus } from 'react-scale/components/scalers/tasks/textcollection/CategoryChooseMultipleAcceptableAudit';
import { QueryableTextAndResultsFieldSectionPreview } from 'react-scale/components/scalers/tasks/textcollection/QueryableTextAndResultsFieldSectionPreview';

interface FieldPreviewProps {
  field: TextCollectionField;
  annotation: TextCollectionAnnotation;
  editDisabled?: boolean;
  onRequestEdit?: () => void;
  attachment: TextCollectionAttachment;
}

const selectedChoiceGroupStyle = {
  border: `1px solid ${Colors.RemoGray10}`,
  borderRadius: '4px',
  margin: '4px',
  padding: '8px 8px 8px 0px', // all but left has padding
};

export default function FieldPreview({
  field,
  annotation,
  editDisabled,
  onRequestEdit,
  attachment,
}: FieldPreviewProps) {
  const styles = useFieldPreviewStyles();
  if (!annotation) {
    return null;
  }
  const count = annotation.response?.length ?? 0;
  const responses = [];
  for (let i = 0; i < count; i++) {
    const label = count > 1 ? `${field.title} [${i}]:` : `${field.title}:`;
    responses.push(
      <div key={i} className={styles.preview}>
        <span dangerouslySetInnerHTML={{ __html: Markdown.renderInline(label) }} />{' '}
        <AnnotationResponse
          annotation={annotation}
          index={i}
          field={field}
          attachment={attachment}
        />
        {!editDisabled && (
          <Link classes={{ root: styles.editLink }} onClick={onRequestEdit}>
            Edit
          </Link>
        )}
      </div>,
    );
  }
  return <div>{responses}</div>;
}

interface AnnotationResponseProps {
  annotation: TextCollectionAnnotation;
  field: TextCollectionField;
  index: number;
  attachment: TextCollectionAttachment;
}

const getDatetimeString = (annotation: DatetimeAnnotation): string => {
  let result = '';
  if (!_.isNil(annotation.hour)) {
    // We don't use mod so that 24 doesn't become 0 AM,
    // and we want to distinguish for consistency.
    const shiftedValue = annotation.hour - 12 * +(annotation.hour > 12);
    const hourType = annotation.hour > 12 ? 'PM' : 'AM';
    result += `${shiftedValue} ${hourType}`;
  }
  return result;
};

const getTimeRangeStringFormatting = (time: number, hourType: string): string => {
  const hours = (Math.floor(time / 3600) || 12).toString();
  const minutes = Math.floor((time % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const timeStr = `${hours}:${minutes}`;
  return `${timeStr} ${hourType}`;
};

const getTimeRangeString = (response: [number, number]): string => {
  const [initial, end] = response;
  const initialTime = getShiftedTime(initial);
  const endTime = getShiftedTime(end);
  return (
    getTimeRangeStringFormatting(initialTime.time, initialTime.hourType) +
    ' to ' +
    getTimeRangeStringFormatting(endTime.time, endTime.hourType)
  );
};

const getCategoryAnnotation = (
  choices: CategoryChoiceValue[],
  field: CategoryField,
  responseStyle: string,
) => {
  if (choices.length === 1) {
    return <span className={responseStyle}>{getCategoryFullLabelPath(choices[0], field)}</span>;
  }
  return (
    <div className={responseStyle}>
      {choices.map &&
        choices.map((value, index) => {
          return (
            <div key={`category-${field.field_id}-${index}`}>
              {'- '}
              {getCategoryFullLabelPath(value, field)}
            </div>
          );
        })}
    </div>
  );
};

export const getCategoryAnnotationChecked = (
  choices: CategoryChoiceValue[],
  updateResponse: (answer: CategoryChoiceValue[]) => void,
  field: CategoryField,
  setNodesInPath: React.Dispatch<React.SetStateAction<string[]>>,
  responseSetToNodesInPath: (response: Set<CategoryChoiceValue>) => string[],
  findAndSetLongHints: (value: CategoryChoiceValue | undefined) => void,
  styleSelectedChoices?: boolean,
  disabled?: boolean,
) => {
  return (
    <div>
      {choices.map &&
        choices.map((value, index) => {
          return (
            <Checkbox
              key={`category-${field.field_id}-${index}`}
              checked={true}
              labelPlacement="end"
              label={<div>{getCategoryFullLabelPath(value, field)}</div>}
              onChange={_e => {
                const newResponse = new Set(choices);
                newResponse.delete(value);
                updateResponse([...newResponse]);
                setNodesInPath(responseSetToNodesInPath(newResponse));
                findAndSetLongHints(undefined);
              }}
              disabled={disabled}
              style={styleSelectedChoices ? selectedChoiceGroupStyle : {}}
              checkBoxColor="rgba(106, 93, 249)"
            />
          );
        })}
    </div>
  );
};

export const getCategoryAnnotationCheckedGroupLabels = (
  choices: CategoryChoiceValue[],
  updateResponse: (answer: CategoryChoiceValue[]) => void,
  field: CategoryField,
  setNodesInPath: React.Dispatch<React.SetStateAction<string[]>>,
  responseSetToNodesInPath: (response: Set<CategoryChoiceValue>) => string[],
  findAndSetLongHints: (value: CategoryChoiceValue | undefined) => void,
  disabled?: boolean,
) => {
  const categoryPathLabelGroups = getCategoryPathLabelGroups(field.choices, choices);
  return (
    <div>
      {Object.keys(categoryPathLabelGroups).map((leafLabel, index) => {
        const groupCategoryFullLabelPaths = categoryPathLabelGroups[leafLabel].map(choice =>
          getCategoryFullLabelPath(choice, field),
        );
        return (
          <Checkbox
            key={`category-${field.field_id}-${index}`}
            checked={true}
            labelPlacement="end"
            label={
              <div>
                {groupCategoryFullLabelPaths.map((path, index) => (
                  <div key={`subcategory-${field.field_id}-${index}-${path}`}>
                    {index !== 0 ? <hr color={Colors.RemoGray10} /> : null}
                    {path}
                  </div>
                ))}
              </div>
            }
            style={selectedChoiceGroupStyle}
            onChange={_e => {
              const newResponse = new Set(choices);
              categoryPathLabelGroups[leafLabel].forEach(value => newResponse.delete(value));
              updateResponse([...newResponse]);
              setNodesInPath(responseSetToNodesInPath(newResponse));
              findAndSetLongHints(undefined);
            }}
            disabled={disabled}
            checkBoxColor="rgba(106, 93, 249)"
          />
        );
      })}
    </div>
  );
};

export const getAcceptableCustomerAuditGroupLabels = (
  choices: CategoryChoiceValue[],
  field: CategoryField,
  status: MultipleCategoryAuditStatus,
  statusMap: Record<string, MultipleCategoryAuditStatus>,
  selectedChoices: CategoryChoiceValue[],
  setSelectedChoices: (selectedChoices: CategoryChoiceValue[]) => void,
  disabled?: boolean,
) => {
  const categoryPathLabelGroups = getCategoryPathLabelGroups(
    field.choices,
    (choices as string[]).filter(choice => statusMap[choice] === status),
  );

  return (
    <div>
      {Object.keys(categoryPathLabelGroups).map((leafLabel, index) => {
        const groupCategoryFullLabelPaths = categoryPathLabelGroups[leafLabel].map(choice =>
          getCategoryFullLabelPath(choice, field),
        );
        const checked = (categoryPathLabelGroups[leafLabel] as string[]).some(groupChoice => {
          return selectedChoices.findIndex(selectedChoice => selectedChoice === groupChoice) !== -1;
        });
        return (
          <Checkbox
            key={`category-${field.field_id}-${index}`}
            checked={checked}
            labelPlacement="end"
            label={
              <div>
                {groupCategoryFullLabelPaths.map((path, index) => (
                  <div key={`subcategory-${field.field_id}-${index}-${path}`}>
                    {index !== 0 ? <hr color={Colors.RemoGray10} /> : null}
                    {path}
                  </div>
                ))}
              </div>
            }
            style={selectedChoiceGroupStyle}
            onChange={_e => {
              (categoryPathLabelGroups[leafLabel] as string[]).forEach(groupChoice => {
                const index = selectedChoices.findIndex(
                  selectedChoice => selectedChoice === groupChoice,
                );
                if (index === -1) {
                  selectedChoices.push(groupChoice);
                } else {
                  selectedChoices.splice(index, 1);
                }
              });
              setSelectedChoices(selectedChoices);
            }}
            disabled={disabled}
          />
        );
      })}
    </div>
  );
};

const getTextForSelectAnnotation = (
  lookupAnnotation: (fieldId: string) => AnnotationLookupResult | undefined,
  annotation: SelectFieldAnnotation,
  index: number,
  field: SelectField,
): JSX.Element | string => {
  const text = annotation.response[index];
  if (field.choices_from_field) {
    const result = lookupAnnotation(field.choices_from_field);
    if (result) {
      const resultAnnotation = result.annotation as FieldSetAnnotation;
      const matchIndex = _.findIndex(resultAnnotation.uuids, uuid => uuid === text);
      if (matchIndex >= 0) {
        return getFieldSetTitle(result.field as FieldSet, resultAnnotation.response[matchIndex]);
      } else {
        return <span style={{ color: '#EA5F28', fontWeight: 500 }}>Missing</span>;
      }
    }
  }
  return text;
};

// eslint-disable-next-line complexity
function AnnotationResponse({ annotation, index, field, attachment }: AnnotationResponseProps) {
  const { lookupAnnotation } = useAnnotationLookup();
  const styles = useFieldPreviewStyles();
  const isRequiredAndMissing = field.required && isBlankField(annotation);
  switch (annotation.type) {
    case TextCollectionFieldType.Text:
    case TextCollectionFieldType.Tool:
    case TextCollectionFieldType.Rubric:
    case TextCollectionFieldType.Autocomplete:
      return (
        <span
          className={classnames(styles.response, {
            [styles.missing]: isRequiredAndMissing,
          })}
        >
          {isRequiredAndMissing
            ? 'Missing'
            : (annotation.response[index] as unknown as React.ReactNode)}
        </span>
      );
    case TextCollectionFieldType.Number:
      return (
        <span
          className={classnames(styles.response, {
            [styles.missing]: isRequiredAndMissing,
          })}
        >
          {isRequiredAndMissing ? 'Missing' : annotation.response[index]}
        </span>
      );
    case TextCollectionFieldType.Category:
      return (
        <span
          className={classnames(styles.response, {
            [styles.missing]: isRequiredAndMissing,
          })}
        >
          {isRequiredAndMissing
            ? 'Missing'
            : getCategoryAnnotation(
                annotation.response[index],
                field as CategoryField,
                styles.response,
              )}
        </span>
      );
    case TextCollectionFieldType.Select:
      return (
        <span
          className={classnames(styles.response, {
            [styles.missing]: isRequiredAndMissing,
          })}
        >
          {isRequiredAndMissing
            ? 'Missing'
            : getTextForSelectAnnotation(lookupAnnotation, annotation, index, field as SelectField)}
        </span>
      );
    case TextCollectionFieldType.Datetime:
      return (
        <span className={styles.response}>{getDatetimeString(annotation.response[index])}</span>
      );
    case TextCollectionFieldType.TimeRange:
      return (
        <span className={styles.response}>{getTimeRangeString(annotation.response[index])}</span>
      );
    case TextCollectionFieldType.Boolean:
      return (
        <span className={styles.response}>{annotation.response[index] ? 'True' : 'False'}</span>
      );
    case TextCollectionFieldType.BoundingBox:
      return (
        <span className={styles.response}>
          {BoundingBoxPreview(annotation.response[index], attachment)}
        </span>
      );
    case TextCollectionFieldType.QueryableTextAndResults:
      return <QueryableTextAndResultsFieldSectionPreview response={annotation.response[index]} />;
    case TextCollectionFieldType.FileUpload:
    case TextCollectionFieldType.AudioRecording:
      return (
        <span
          className={classnames(styles.response, {
            [styles.missing]: isRequiredAndMissing,
          })}
        >
          {isRequiredAndMissing ? 'Missing' : annotation.response[index].name}
        </span>
      );
    case TextCollectionFieldType.Code:
      return (
        <span
          className={classnames(styles.response, {
            [styles.missing]: isRequiredAndMissing,
          })}
        >
          {isRequiredAndMissing ? 'Missing' : annotation.response[index].sourceCode.slice(0, 200)}
        </span>
      );
    case TextCollectionFieldType.WorkspaceContainer:
      return (
        <span
          className={classnames(styles.response, {
            [styles.missing]: isRequiredAndMissing,
          })}
        >
          {isRequiredAndMissing ? 'Missing' : 'Workspace IDE'}
        </span>
      );
    default:
      return (
        <span className={styles.missing}>
          Error: Missing renderAnnotationResponse for "{annotation.type}" type.
        </span>
      );
  }
}

const useFieldPreviewStyles = makeStyles({
  preview: {
    fontSize: '14px',
    lineHeight: '18px',
    color: '#808080',
    padding: '4px 8px',
    fontWeight: 400,
    '&:hover $editLink': {
      visibility: 'visible',
    },
  },
  response: {
    color: '#000000',
  },
  missing: {
    color: 'red',
  },
  editLink: {
    color: '#2B99D8',
    fontSize: '14px',
    lineHeight: '16px',
    marginLeft: 16,
    marginRight: 16,
    visibility: 'hidden',
  },
});
